import * as React from "react";
import {
  Box,
  Grid,
  InputLabel,
  Link,
  Radio,
  Select,
  TextField,
} from "@mui/material";
import { PrimaryInsuranceType } from "../types";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { ImagesContext } from "../../Context";
import globals from "../../globals/globals";
import moment from "moment";
import InputMask from "react-input-mask";
// --- Helper Imports ---
import { 
  getNameError, 
  MAX_NAME_LENGTH,
  getAddressLine2Error,
  getAddressError,
  MAX_ADDRESS_LENGTH,
  getCityError, 
  getPhoneNumberError,
  MAX_PHONE_LENGTH,
  getZipError,
  ZIP_LENGTH,
  sanitizeZip,
} from "../../utils/DemographicValidationHelper";


interface PrimaryInsuranceEditProps {
  primaryInsurance: PrimaryInsuranceType | null;
  insuranceTypeOptions: any;
  handlePrimaryInsuranceChange: (
    updated: Partial<PrimaryInsuranceType>
  ) => void;
}

const PrimaryInsuranceEdit: React.FC<PrimaryInsuranceEditProps> = ({
  primaryInsurance,
  insuranceTypeOptions,
  handlePrimaryInsuranceChange,
}) => {
  const images = React.useContext(ImagesContext);
  const [openDateOfBirth, setOpenDateOfBirth] = React.useState<boolean>(false);

  const stateOptions = globals.states.map((state) => {
    return (
      <option key={state} value={state}>
        {state}
      </option>
    );
  });

  const insuranceTypes = insuranceTypeOptions?.map((option) => {
    return (
      <option key={option?.insurance_type} value={option?.insurance_type}>
        {option?.insurance_type}
      </option>
    );
  });

  const handleDateOfBirthChange = (date: moment.Moment | null) => {
    handlePrimaryInsuranceChange({
      insured_dob: date ? date.format("YYYY-MM-DD") : "",
    });
  };

  return (
    <Box className="edit-container">
      <Grid item xs={12} className="claim-edit-container">
        <Grid container>
          <Grid container className="claim-form-container">
            <Grid item xs={2.5} sx={{ mr: 5 }}>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="last_name" className="field-label">
                    Insurance Type*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <Select
                    id="insurance_type"
                    value={primaryInsurance?.insurance_type ?? ""}
                    required
                    variant="outlined"
                    className="the-field"
                    onChange={(e) =>
                      handlePrimaryInsuranceChange({
                        insurance_type: e.target.value,
                      })
                    }
                    native
                  >
                    <option value={""}>Select Insurance</option>
                    {insuranceTypes}
                  </Select>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="first_name" className="field-label">
                    Insurance Plan or Program*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="first_name"
                    size="small"
                    value={primaryInsurance?.plan_name ?? ""}
                    autoComplete="new-password"
                    className="the-field"
                    required
                    variant="outlined"
                    inputProps={{ maxLength: 100 }}
                    error={
                      primaryInsurance?.plan_name &&
                      primaryInsurance.plan_name.length > 100
                    }
                    helperText={
                      primaryInsurance?.plan_name &&
                      primaryInsurance.plan_name.length > 100
                        ? "Plan name must be 100 characters or less"
                        : ""
                    }
                    onChange={(e) => {
                      const value = e.target.value;

                      // Local validation stays here
                      if (value.length <= 100) {
                        handlePrimaryInsuranceChange({ plan_name: value });
                      }

                      // If you want to allow typing past 100 but just show an error,
                      // remove the if-statement above and always pass value upward.
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="middle_name" className="field-label">
                    Insurance Number*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                 <TextField
                    id="middle_name"
                    size="small"
                    value={primaryInsurance?.insurance_number ?? ""}
                    className="the-field"
                    required
                    autoComplete="off"
                    variant="outlined"
                    inputProps={{ maxLength: 30 }}
                    error={
                      primaryInsurance?.insurance_number &&
                      !/^[a-zA-Z0-9]*$/.test(primaryInsurance.insurance_number)
                    }
                    helperText={
                      primaryInsurance?.insurance_number &&
                      !/^[a-zA-Z0-9]*$/.test(primaryInsurance.insurance_number)
                        ? "Insurance number must be alphanumeric"
                        : ""
                    }
                    onChange={(e) => {
                      const raw = e.target.value;
                      const cleaned = raw.replace(/[^a-zA-Z0-9]/g, "").slice(0, 30);

                      handlePrimaryInsuranceChange({
                        insurance_number: cleaned,
                      });
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container className="field-container">
                <Grid item xs={12}>
                  <InputLabel htmlFor="gender" className="field-label">
                    Patient’s Relationship to Insured*
                  </InputLabel>
                </Grid>
                <Grid item xs={12}>
                  <div className="row" style={{ height: 40 }}>
                    <Radio
                      checked={primaryInsurance?.relationship === "self"}
                      onChange={(e) =>
                        handlePrimaryInsuranceChange({ relationship: "self" })
                      }
                      value="self"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Self" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{
                        color: "#A29D9B",
                        ml: 2,
                        mr: 1,
                        width: 15,
                        height: 15,
                      }}
                    />
                    <p className="radio-label">Self</p>
                    <Radio
                      checked={primaryInsurance?.relationship === "spouse"}
                      onChange={(e) =>
                        handlePrimaryInsuranceChange({ relationship: "spouse" })
                      }
                      value="spouse"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Spouse" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{
                        color: "#A29D9B",
                        ml: 2,
                        mr: 1,
                        width: 15,
                        height: 15,
                      }}
                    />
                    <p className="radio-label">Spouse</p>
                    <Radio
                      checked={primaryInsurance?.relationship === "child"}
                      onChange={(e) =>
                        handlePrimaryInsuranceChange({ relationship: "child" })
                      }
                      value="child"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Child" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{
                        color: "#A29D9B",
                        ml: 2,
                        mr: 1,
                        width: 15,
                        height: 15,
                      }}
                    />
                    <p className="radio-label">Child</p>
                    <Radio
                      checked={primaryInsurance?.relationship === "other"}
                      onChange={(e) =>
                        handlePrimaryInsuranceChange({ relationship: "other" })
                      }
                      value="other"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Other" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{
                        color: "#A29D9B",
                        ml: 2,
                        mr: 1,
                        width: 15,
                        height: 15,
                      }}
                    />
                    <p className="radio-label">Other</p>
                  </div>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={2.5} sx={{ mr: 5 }}>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="last_name" className="field-label">
                    Insured’s Last Name*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="last_name"
                    size="small"
                    value={primaryInsurance?.last_name ?? ""}
                    className="the-field"
                    required
                    autoComplete="new-password"
                    variant="outlined"
                    inputProps={{ maxLength: MAX_NAME_LENGTH }}
                    error={Boolean(getNameError(primaryInsurance?.last_name ?? "", "Last name"))}
                    helperText={getNameError(primaryInsurance?.last_name ?? "", "Last name")}
                    onChange={(e) =>
                      handlePrimaryInsuranceChange({
                        last_name: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="first_name" className="field-label">
                    Insured’s First Name*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="first_name"
                    size="small"
                    value={primaryInsurance?.first_name ?? ""}
                    className="the-field"
                    required
                    autoComplete="new-password"
                    variant="outlined"
                    inputProps={{ maxLength: MAX_NAME_LENGTH }}
                    {...(() => {
                      const errorText = getNameError(primaryInsurance?.first_name ?? "", "First name");
                      return {
                        error: Boolean(errorText),
                        helperText: errorText,
                      };
                    })()}
                    onChange={(e) =>
                      handlePrimaryInsuranceChange({
                        first_name: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="middle_name" className="field-label">
                    Insured’s Middle Name*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="middle_name"
                    size="small"
                    value={primaryInsurance?.middle_name ?? ""}
                    className="the-field"
                    required
                    autoComplete="off"
                    variant="outlined"
                    inputProps={{ maxLength: MAX_NAME_LENGTH }}
                    {...(() => {
                      const errorText = getNameError(primaryInsurance?.middle_name ?? "", "Middle name");
                      return {
                        error: Boolean(errorText),
                        helperText: errorText,
                      };
                    })()}
                    onChange={(e) =>
                      handlePrimaryInsuranceChange({
                        middle_name: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={2.5} sx={{ mr: 5 }}>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="address" className="field-label">
                    Address Line One*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="address"
                    size="small"
                    value={primaryInsurance?.address_line_1 ?? ""}
                    required
                    variant="outlined"
                    autoComplete="new-password"
                    className="the-field"
                    inputProps={{ maxLength: MAX_ADDRESS_LENGTH }}
                    {...getAddressError(primaryInsurance?.address_line_1 ?? "")}
                    onChange={(e) =>
                      handlePrimaryInsuranceChange({
                        address_line_1: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="address" className="field-label">
                    Address Line Two
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                 <TextField
                    id="address"
                    size="small"
                    autoComplete="new-password"
                    value={primaryInsurance?.address_line_2 ?? ""}
                    required
                    variant="outlined"
                    className="the-field"
                    {...getAddressLine2Error(primaryInsurance?.address_line_2 ?? "")}
                    onChange={(e) =>
                      handlePrimaryInsuranceChange({
                        address_line_2: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="city" className="field-label">
                    City*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="city"
                    size="small"
                    autoComplete="new-password"
                    value={primaryInsurance?.city ?? ""}
                    required
                    variant="outlined"
                    className="the-field"
                    {...getCityError(primaryInsurance?.city ?? "")}
                    onChange={(e) =>
                      handlePrimaryInsuranceChange({ city: e.target.value })
                    }
                  />
                </Grid>
              </Grid>

              <Grid container justifyContent="space-between">
                <Grid container xs={5.5}>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="state" className="field-label">
                      State*
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} className="field-container">
                    <Select
                      id="state"
                      value={primaryInsurance?.state || ""}
                      required
                      variant="outlined"
                      className="the-field"
                      onChange={(e) =>
                        handlePrimaryInsuranceChange({ state: e.target.value })
                      }
                      native
                    >
                      <option value={""}>Select</option>
                      {stateOptions}
                    </Select>
                  </Grid>
                </Grid>
                <Grid container xs={5.5} sx={{ mr: 1 }}>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="zip" className="field-label">
                      ZIP Code*
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} className="field-container">
                    <TextField
                      id="zip"
                      placeholder="Enter Zip"
                      size="small"
                      value={primaryInsurance?.zip ?? ""}
                      required
                      variant="outlined"
                      autoComplete="new-password"
                      className="the-field"
                      {...getZipError(primaryInsurance?.zip ?? "")}
                      onChange={(e) => {
                        const zipCode = sanitizeZip(e.target.value);
                        handlePrimaryInsuranceChange({ zip: zipCode });
                      }}
                      slotProps={{
                        htmlInput: {
                          inputMode: "numeric",
                          maxLength: ZIP_LENGTH,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={2.5} sx={{ mr: 5 }}>
              <Box>
                <InputLabel htmlFor="dateOfBirth" className="field-label">
                  Date of Birth*
                </InputLabel>
                <Box
                  className="field-container"
                  sx={{
                    marginLeft: "0px",
                    backgroundColor: "#efe9e7",
                    display: "flex",
                    borderRadius: "5px",
                    paddingRight: "10.5px",
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Box
                      className="date-box"
                      sx={{
                        width: "88%",
                        maxHeight: 41.13,
                      }}
                    >
                      <DemoContainer components={["DatePicker", "DatePicker"]}>
                        <DatePicker
                          open={openDateOfBirth}
                          onOpen={() => setOpenDateOfBirth(true)}
                          onClose={() => setOpenDateOfBirth(false)}
                          value={
                            primaryInsurance?.insured_dob
                              ? moment(primaryInsurance.insured_dob)
                              : null
                          }
                          onChange={(x) => {
                            handleDateOfBirthChange(x);
                          }}
                          format="MM/DD/YYYY"
                          disableOpenPicker
                          disabled={false}
                          slotProps={{
                            textField: {
                              placeholder: "Date of Birth",
                            },
                            openPickerIcon: images.calendar_icon,
                          }}
                          className="report-date-input no-border-outline"
                          maxDate={null}
                        />
                      </DemoContainer>
                    </Box>
                  </LocalizationProvider>
                  <Link
                    className="invite-app-user-link"
                    onClick={() => setOpenDateOfBirth(true)}
                  >
                    <img
                      src={images.calendar_icon}
                      alt="Calendar"
                      className="action-icon-image"
                      style={{ width: 22.75, maxHeight: 26 }}
                    />
                  </Link>
                </Box>
              </Box>

              <Grid container>
                <Grid item xs={12}>
                  <InputLabel
                    htmlFor="mobile_phone_number"
                    className="field-label"
                  >
                    Phone Number*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <InputMask
                    id="mobile-phone-number"
                    mask="(999)-999-9999"
                    value={primaryInsurance?.phone_number ?? ""}
                    disabled={false}
                    maskChar=""
                    variant="outlined"
                    className="the-field"
                    size="small"
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, MAX_PHONE_LENGTH);
                      handlePrimaryInsuranceChange({ phone_number: digitsOnly });
                    }}
                  >
                    {() => (
                      <TextField
                        variant="outlined"
                        size="small"
                        className="the-field"
                        autoComplete="off"
                        {...getPhoneNumberError(primaryInsurance?.phone_number ?? "")}
                      />
                    )}
                  </InputMask>
                </Grid>
              </Grid>

              <Grid container className="field-container">
                <Grid item xs={12}>
                  <InputLabel htmlFor="gender" className="field-label">
                    Sex*
                  </InputLabel>
                </Grid>
                <Grid item xs={12}>
                  <div className="row" style={{ height: 40 }}>
                    <Radio
                      checked={primaryInsurance?.gender === "Male"}
                      onChange={(e) =>
                        handlePrimaryInsuranceChange({ gender: "Male" })
                      }
                      value="Male"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Male" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{
                        color: "#A29D9B",
                        ml: 1,
                        width: 32,
                        height: 32,
                      }}
                    />
                    <p className="radio-label">Male</p>
                    <Radio
                      checked={primaryInsurance?.gender === "Female"}
                      onChange={(e) =>
                        handlePrimaryInsuranceChange({ gender: "Female" })
                      }
                      value="Female"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Female" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{
                        color: "#A29D9B",
                        ml: 2,
                        width: 32,
                        height: 32,
                      }}
                    />
                    <p className="radio-label">Female</p>
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PrimaryInsuranceEdit;
